'use client';
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import TopBar from '@/components/TopBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const benefits = [
{
  title: 'Decades of Cross-Industry Experience',
  description: 'Our advisors have worked across real estate, manufacturing, healthcare, technology, and trade finance — bringing pattern recognition that sector-specific advisors lack.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>

},
{
  title: 'Objective, Conflict-Free Advice',
  description: 'We are not a bank, not a fund, and not a product seller. Our only incentive is your outcome — which means advice you can actually trust.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
      </svg>

},
{
  title: 'Actionable Financial Models',
  description: "We don't deliver 80-page reports that gather dust. Every engagement ends with a clear financial model, decision framework, and implementation roadmap.",
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>

},
{
  title: 'M&A Advisory Expertise', description: 'Whether you are acquiring a competitor or preparing your business for sale, we manage the full process from valuation to deal close.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
      </svg>

},
{
  title: 'Capital Structure Optimization', description: 'We analyze your debt-to-equity mix, cost of capital, and covenant headroom to identify where you are over-leveraged or leaving cheap capital on the table.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>

},
{
  title: 'Restructuring & Turnaround', description: 'When a business faces financial distress, speed and precision matter. We develop restructuring plans that protect value and preserve stakeholder relationships.',
  icon:
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>

}];


const processSteps = [
{
  step: '01',
  title: 'Discovery & Diagnosis',
  description: 'We conduct a thorough review of your financial statements, capital structure, strategic objectives, and market position to identify the core challenge or opportunity.'
},
{
  step: '02',
  title: 'Strategic Framework',
  description: 'We develop a clear strategic framework — with defined objectives, success metrics, and decision criteria — before any recommendations are made.'
},
{
  step: '03',
  title: 'Financial Modeling',
  description: 'We build detailed financial models that stress-test scenarios, quantify risks, and project outcomes under multiple assumptions.'
},
{
  step: '04',
  title: 'Recommendation Delivery',
  description: 'We present findings and recommendations in a clear, executive-ready format — with supporting data, trade-off analysis, and a prioritized action plan.'
},
{
  step: '05',
  title: 'Implementation Support',
  description: "Advisory doesn't stop at the presentation. We support implementation — whether that means negotiating with lenders, managing M&A processes, or guiding internal teams."
},
{
  step: '06', title: 'Ongoing Advisory Retainer', description: 'For clients who want a trusted financial partner on call, we offer retainer arrangements that provide continuous access to our advisory team.'
}];


const advisoryAreas = [
'Capital Structure Optimization',
'Mergers & Acquisitions',
'Financial Restructuring',
'Strategic Planning',
'Investor Relations',
'Debt Refinancing Strategy',
'Business Valuation',
'Exit Planning'];


function AnimatedSection({ children, className = '' }: {children: React.ReactNode;className?: string;}) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {if (entry.isIntersecting) setVisible(true);}, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}>
      {children}
    </div>);

}

export default function FinancialAdvisoryPage() {
  return (
    <main className="min-h-screen font-sans bg-white">
      <TopBar />
      <Header />

      {/* Hero */}
      <section className="relative w-full min-h-[480px] flex items-center justify-center overflow-hidden">
        <Image
          src="https://img.rocket.new/generatedImages/rocket_gen_img_192e2dc63-1766777352505.png"
          alt="Financial advisors in a strategic planning meeting"
          fill
          className="object-cover"
          priority />
        
        <div className="absolute inset-0 bg-brand-darkbg/75" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center py-24">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-12 bg-brand-green" />
            <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">Service 03</span>
            <div className="h-px w-12 bg-brand-green" />
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-medium text-white mb-6 leading-tight">
            Financial Advisory Services
          </h1>
          <p className="font-sans text-gray-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Strategic financial consulting that optimizes your capital structure, guides M&A decisions, and builds the financial foundation for long-term growth.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/#contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-green text-white rounded-md font-sans font-semibold text-sm hover:bg-green-600 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand-green/30">
              
              Speak With an Advisor
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              href="/#services"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/30 text-white rounded-md font-sans font-semibold text-sm hover:bg-white/10 transition-all duration-200">
              
              All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Deep Dive */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-0.5 bg-brand-green" />
                <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">What We Do</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-medium text-brand-navy mb-6">
                Strategic Advice That Changes the Trajectory of Your Business
              </h2>
              <p className="font-sans text-gray-600 text-base leading-relaxed mb-5">
                Most businesses make their most consequential financial decisions — raising capital, acquiring companies, restructuring debt, planning an exit — without a dedicated financial advisor in their corner. The result is suboptimal terms, missed opportunities, and avoidable mistakes.
              </p>
              <p className="font-sans text-gray-600 text-base leading-relaxed mb-5">
                Grid Global's financial advisory practice provides the strategic counsel that business owners and executives need at critical inflection points. We bring an outside perspective, deep financial expertise, and a network of capital providers to every engagement.
              </p>
              <p className="font-sans text-gray-600 text-base leading-relaxed">
                Our advisory work is always grounded in rigorous financial analysis — not generic frameworks. We build the models, stress-test the assumptions, and deliver recommendations that are specific to your business, your market, and your goals.
              </p>
            </AnimatedSection>
            <AnimatedSection className="grid grid-cols-2 gap-4">
              {advisoryAreas.map((area, i) =>
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50 hover:border-brand-green/30 hover:bg-brand-green/5 transition-all duration-200">
                  <div className="w-2 h-2 rounded-full bg-brand-green flex-shrink-0" />
                  <span className="font-sans text-sm text-brand-navy font-medium">{area}</span>
                </div>
              )}
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-brand-darkbg">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-brand-green" />
              <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">Why Grid Global</span>
              <div className="h-px w-12 bg-brand-green" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-white">
              The Advantages of Working With Us
            </h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, i) =>
            <AnimatedSection key={i} className="p-6 rounded-2xl bg-brand-altbg border border-white/10 hover:border-brand-green/40 transition-all duration-300 group">
                <div className="w-12 h-12 rounded-xl bg-brand-green/20 flex items-center justify-center text-brand-green mb-5 group-hover:bg-brand-green group-hover:text-white transition-all duration-300">
                  {benefit.icon}
                </div>
                <h3 className="font-serif text-lg font-medium text-white mb-3">{benefit.title}</h3>
                <p className="font-sans text-gray-400 text-sm leading-relaxed">{benefit.description}</p>
              </AnimatedSection>
            )}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-brand-green" />
              <span className="font-sans text-brand-green text-sm font-medium tracking-widest uppercase">Our Process</span>
              <div className="h-px w-12 bg-brand-green" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-brand-navy">
              How We Deliver Advisory Engagements
            </h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, i) =>
            <AnimatedSection key={i}>
                <div className="p-8 rounded-2xl border border-gray-100 hover:border-brand-navy/20 hover:shadow-lg transition-all duration-300 h-full">
                  <div className="font-serif text-5xl font-medium text-brand-navy/10 mb-4 leading-none">{step.step}</div>
                  <h3 className="font-serif text-xl font-medium text-brand-navy mb-3">{step.title}</h3>
                  <p className="font-sans text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </AnimatedSection>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-navy">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-white mb-5">
              Facing a Major Financial Decision?
            </h2>
            <p className="font-sans text-gray-300 text-lg mb-10 max-w-2xl mx-auto">
              Whether you're raising capital, planning an acquisition, or restructuring your balance sheet — our advisors are ready to help you think it through.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/#contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-green text-white rounded-md font-sans font-semibold text-sm hover:bg-green-600 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand-green/30">
                
                Schedule a Consultation
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                href="/#services"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/30 text-white rounded-md font-sans font-semibold text-sm hover:bg-white/10 transition-all duration-200">
                
                Explore Other Services
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </main>);

}